import React from 'react'
import { type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'
import { match } from 'ts-pattern'

import {
  FilterGroupsInput,
  type FilterGroupsInputProps,
} from '~/components/features/FilterGroupsInput'
import {
  type ConditionOperation,
  type SurveyGroupCustomerAttributeFilterCategory,
} from '~/gql/graphql'
import {
  compact,
  conditionOperationLabelI18nKeyMap,
  findByPath,
  formatNineSegment,
  matchSurveyGroupFilter,
  type NineNumber,
  pathOr,
  type SurveyGroupFilterItem,
} from '~/utils'

import { BrandSegmentInput } from './components/BrandSegmentInput'
import { CustomerAttributeGroupInput } from './components/CustomerAttributeGroupInput'
import { QuestionInput } from './components/QuestionInput'
import {
  type InputValues,
  type SurveyGroupBrand,
  type SurveyGroupCustomerAttributeGroup,
  type SurveyGroupQuestion,
} from './utils'

export type SurveyGroupFilterGroupsInputProps = {
  brands: SurveyGroupBrand[]
  control: Control<InputValues>
  customerAttributeGroups: SurveyGroupCustomerAttributeGroup[]
  'data-testid'?: string
  questions: SurveyGroupQuestion[]
}

export const SurveyGroupFilterGroupsInput = ({
  brands,
  control,
  customerAttributeGroups,
  'data-testid': dataTestId = 'components-features-survey-group-filter-groups-input',
  questions,
}: SurveyGroupFilterGroupsInputProps) => {
  const { t } = useTranslation()
  const addFilterButtonItems = React.useMemo<
    FilterGroupsInputProps<
      SurveyGroupFilterItem,
      SurveyGroupCustomerAttributeFilterCategory
    >['addFilterButtonItems']
  >(
    () =>
      compact([
        0 < questions.length && {
          label: t('select_from_questionnaire_response_3'),
          value: 'Question',
        },
        0 < customerAttributeGroups.length && {
          label: t('select_from_customized_segments_3'),
          value: 'CustomerAttribute',
        },
        0 < brands.length && {
          label: t('select_from_9segs_1'),
          value: 'BrandSegments',
        },
      ]),
    [brands.length, customerAttributeGroups.length, questions.length, t],
  )

  const categoryToFilter = React.useMemo<
    FilterGroupsInputProps<
      SurveyGroupFilterItem,
      SurveyGroupCustomerAttributeFilterCategory
    >['categoryToFilter']
  >(
    () => (category) =>
      match(category)
        .with('Question', (category) => {
          const question = questions[0]
          const answerOption = question.answerOptions[0]

          return {
            category,
            question: {
              surveyGroupQuestionId: question.id,
              operation: 'equal' as ConditionOperation,
              answerOptions: [answerOption],
            },
          }
        })
        .with('CustomerAttribute', (category) => {
          const customerAttributeGroup = customerAttributeGroups[0]
          const customerAttribute = customerAttributeGroup.customerAttributes[0]

          return {
            category,
            customerAttribute: {
              surveyGroupCustomerAttributeGroupId: customerAttributeGroup.id,
              operation: 'equal' as ConditionOperation,
              customerAttributes: [customerAttribute],
            },
          }
        })
        .with('BrandSegments', (category) => {
          const brand = brands[0]

          return {
            category,
            brandSegments: {
              surveyGroupBrandId: brand.id,
              segmentNumbers: [],
            },
          }
        })
        .exhaustive(),
    [brands, customerAttributeGroups, questions],
  )

  const filterToText = React.useMemo<
    FilterGroupsInputProps<
      SurveyGroupFilterItem,
      SurveyGroupCustomerAttributeFilterCategory
    >['filterToText']
  >(
    () => (filter) =>
      matchSurveyGroupFilter(filter, {
        question: (question) => {
          const questionText = R.pipe(
            questions,
            findByPath('id', question.surveyGroupQuestionId),
            pathOr('name', t('unknown_questionnaire')),
          )
          const answerOptionTexts = question.answerOptions.map(R.prop('text'))

          return [
            t('questionnaire_5'),
            questionText,
            ...answerOptionTexts,
            t(conditionOperationLabelI18nKeyMap[question.operation]),
          ].join(' ')
        },
        customerAttribute: (customerAttribute) => {
          const customerAttributeGroupText = R.pipe(
            customerAttributeGroups,
            findByPath(
              'id',
              customerAttribute.surveyGroupCustomerAttributeGroupId,
            ),
            pathOr('name', t('unknown_customized_segments_group')),
          )
          const customerAttributeTexts =
            customerAttribute.customerAttributes.map(R.prop('name'))

          return [
            t('customized_segments_group_4'),
            customerAttributeGroupText,
            ...customerAttributeTexts,
            t(conditionOperationLabelI18nKeyMap[customerAttribute.operation]),
          ].join(' ')
        },
        brandSegments: (brandSegments) => {
          const brand = findByPath(
            brands,
            'id',
            brandSegments.surveyGroupBrandId,
          )
          const nineSegmentTexts = brandSegments.segmentNumbers
            .map((nineNumber) =>
              formatNineSegment(nineNumber as NineNumber, '%t', t),
            )
            .join(',')

          return [t('brand'), brand?.name, nineSegmentTexts].join(' ')
        },
      }),
    [brands, customerAttributeGroups, questions, t],
  )

  return (
    <FilterGroupsInput
      addFilterButtonItems={addFilterButtonItems}
      categoryToFilter={categoryToFilter}
      control={control}
      data-testid={dataTestId}
      filterToText={filterToText}
      renderInput={({ category, filterGroupIndex, filterIndex }) =>
        match(category)
          .with('Question', () => (
            <QuestionInput
              control={control}
              filterGroupIndex={filterGroupIndex}
              filterIndex={filterIndex}
              questions={questions}
            />
          ))
          .with('CustomerAttribute', () => (
            <CustomerAttributeGroupInput
              control={control}
              customerAttributeGroups={customerAttributeGroups}
              filterGroupIndex={filterGroupIndex}
              filterIndex={filterIndex}
            />
          ))
          .with('BrandSegments', () => (
            <BrandSegmentInput
              brands={brands}
              control={control}
              filterGroupIndex={filterGroupIndex}
              filterIndex={filterIndex}
            />
          ))
          .exhaustive()
      }
    />
  )
}
